import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { suppliesService } from '../../services/supplies';
import { productsService } from '../../services/products';
import { useAuth } from '../../hooks/useAuth';

interface NewSupplier {
  id: string;
  name: string;
  orderId: number;
  orderDate: string;
}

const NewSuppliers: React.FC = () => {
  const { user } = useAuth();
  const [newSuppliers, setNewSuppliers] = useState<NewSupplier[]>([]);
  const [existingSuppliers, setExistingSuppliers] = useState<string[]>([]);
  const [selectedExistingSuppliers, setSelectedExistingSuppliers] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [replacing, setReplacing] = useState<{ [key: string]: boolean }>({});

  // Check if user can manage suppliers
  const canManageSuppliers = () => {
    if (!user) return false;
    return user.role === 'Purchase admin' || 
           user.role === 'Purchase supervisor' || 
           user.role === 'Purchase agent' ||
           user.role === 'super admin';
  };

  // Load data
  useEffect(() => {
    if (canManageSuppliers()) {
      loadData();
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get all supply orders to find new suppliers
      const ordersResponse = await suppliesService.getSupplyOrders({ page: 1, limit: 1000 });
      const orders = ordersResponse.orders;
      
      // Get existing suppliers from products table
      const existingSuppliersData = await productsService.getProductSuppliers();
      setExistingSuppliers(existingSuppliersData);
      
      // Find new suppliers (suppliers in orders that are not in products table)
      const allOrderSuppliers = orders
        .map(order => order.supplier)
        .filter(supplier => supplier && supplier.trim() !== '');
      
      const uniqueOrderSuppliers = [...new Set(allOrderSuppliers)];
      
      // Filter to get only new suppliers (not in existing suppliers list)
      const newSuppliersData: NewSupplier[] = [];
      
      uniqueOrderSuppliers.forEach(supplier => {
        if (supplier && !existingSuppliersData.some(existing => 
          existing.toLowerCase() === supplier.toLowerCase()
        )) {
          // Find the most recent order with this supplier
          const recentOrder = orders
            .filter(order => order.supplier === supplier)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
          
          if (recentOrder) {
            newSuppliersData.push({
              id: `${supplier}-${recentOrder.id}`,
              name: supplier,
              orderId: recentOrder.id,
              orderDate: recentOrder.created_at
            });
          }
        }
      });
      
      setNewSuppliers(newSuppliersData);
      
    } catch (error) {
      console.error('Error loading suppliers data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load suppliers data'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExistingSupplierChange = (newSupplierId: string, existingSupplier: string) => {
    setSelectedExistingSuppliers(prev => ({
      ...prev,
      [newSupplierId]: existingSupplier
    }));
  };

  const handleReplace = async (newSupplier: NewSupplier) => {
    const existingSupplier = selectedExistingSuppliers[newSupplier.id];
    
    if (!existingSupplier) {
      Swal.fire({
        icon: 'warning',
        title: 'Warning',
        text: 'Please select an existing supplier to replace with'
      });
      return;
    }

    // Confirm the replacement
    const result = await Swal.fire({
      icon: 'question',
      title: 'Confirm Replacement',
      html: `
        <p>Are you sure you want to replace:</p>
        <p><strong>New Supplier:</strong> ${newSupplier.name}</p>
        <p><strong>With Existing Supplier:</strong> ${existingSupplier}</p>
        <p class="text-muted">This will update all supply orders with this supplier name.</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Replace',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    });

    if (!result.isConfirmed) return;

    try {
      setReplacing(prev => ({ ...prev, [newSupplier.id]: true }));

      // Get all orders with the new supplier name
      const ordersResponse = await suppliesService.getSupplyOrders({ page: 1, limit: 1000 });
      const ordersToUpdate = ordersResponse.orders.filter(order => 
        order.supplier === newSupplier.name
      );

      // Update each order's supplier
      const updatePromises = ordersToUpdate.map(order =>
        suppliesService.updateSupplier(order.id, existingSupplier)
      );

      await Promise.all(updatePromises);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Successfully replaced "${newSupplier.name}" with "${existingSupplier}" in ${ordersToUpdate.length} order(s)`
      });

      // Reload data to reflect changes
      await loadData();
      
    } catch (error) {
      console.error('Error replacing supplier:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to replace supplier'
      });
    } finally {
      setReplacing(prev => ({ ...prev, [newSupplier.id]: false }));
    }
  };

  if (!canManageSuppliers()) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          You don't have permission to manage suppliers.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4>
                <i className="bi bi-truck me-2"></i>
                New Suppliers Management
              </h4>
              <p className="text-muted mb-0">
                Manage new suppliers that don't exist in the products database
              </p>
            </div>
            <button
              className="btn btn-outline-primary"
              onClick={loadData}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {newSuppliers.length === 0 ? (
        <div className="row">
          <div className="col">
            <div className="alert alert-info text-center" role="alert">
              <i className="bi bi-info-circle me-2"></i>
              No new suppliers found. All suppliers in supply orders are already in the products database.
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th className="supplier-th supplier-th-new">New Supplier</th>
                        <th className="supplier-th supplier-th-existing">Existing Supplier</th>
                        <th className="supplier-th supplier-th-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newSuppliers.map((supplier) => (
                        <tr key={supplier.id}>
                          <td>
                            <div>
                              <strong>{supplier.name}</strong>
                              <br />
                              <small className="text-muted">
                                Added: {new Date(supplier.orderDate).toLocaleDateString()}
                              </small>
                            </div>
                          </td>
                          <td>
                            <select
                              className="form-select"
                              value={selectedExistingSuppliers[supplier.id] || ''}
                              onChange={(e) => handleExistingSupplierChange(supplier.id, e.target.value)}
                              disabled={replacing[supplier.id]}
                            >
                              <option value="">Select existing supplier...</option>
                              {existingSuppliers.map((existingSupplier) => (
                                <option key={existingSupplier} value={existingSupplier}>
                                  {existingSupplier}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReplace(supplier)}
                              disabled={
                                replacing[supplier.id] || 
                                !selectedExistingSuppliers[supplier.id]
                              }
                            >
                              {replacing[supplier.id] ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  Replacing...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-arrow-repeat me-2"></i>
                                  Replace
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewSuppliers;
